import React from 'react';
import { Banknote } from 'lucide-react';
import { driverAccessFeeFor } from '../lib/fees';
import { DIRECT_METHOD_LABELS } from './PaymentMethodBadges';

const eur = (n) => `€${(Number(n) || 0).toFixed(2)}`;

const Row = ({ label, value, testId, strong, negative }) => (
  <div className="flex items-baseline justify-between gap-3 py-1" data-testid={testId}>
    <span className={strong ? 'text-sm font-semibold text-gray-900' : 'text-sm text-gray-600'}>{label}</span>
    <span
      className={`shrink-0 tabular-nums whitespace-nowrap ${
        strong ? 'text-base font-extrabold text-[#1B5E20]' : negative ? 'text-sm font-medium text-red-600' : 'text-sm font-medium text-gray-800'
      }`}
    >
      {value}
    </span>
  </div>
);

/**
 * Driver-facing payment breakdown — Pay Driver Directly.
 * The €10 Booking Deposit is NEVER deducted from the driver's payout; the
 * driver's only deduction is the 10% JustDeliver Driver Fee.
 */
export const DriverPaymentBreakdown = ({ job }) => {
  const bid = Number(job.agreed_bid_amount ?? job.assigned_price) || 0;
  if (!bid) return null;

  const deduction = Number(job.driver_deduction_amount ?? job.driver_access_fee ?? driverAccessFeeFor(bid)) || 0;
  const payout = Number(job.driver_payout_amount ?? job.direct_balance_due ?? (bid - deduction));
  const methods = (job.driver_payment_methods || ['cash']).filter((m) => DIRECT_METHOD_LABELS[m]);
  const methodLabels = (methods.length ? methods : ['cash']).map((m) => DIRECT_METHOD_LABELS[m]).join(', ');

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4" data-testid={`driver-payment-breakdown-${job.id}`}>
      <div className="flex items-center gap-2 mb-2.5">
        <Banknote className="w-4 h-4 text-amber-700" />
        <span className="text-xs font-bold uppercase tracking-wider text-amber-900">
          Payment: paid directly to you by the customer
        </span>
      </div>

      <div className="divide-y divide-black/5">
        <Row label="Accepted delivery price" value={eur(bid)} testId={`pb-bid-${job.id}`} />
        <Row
          label={deduction > 0 ? 'JustDeliver Driver Fee (10%)' : 'JustDeliver Driver Fee (bids under €100)'}
          value={deduction > 0 ? `− ${eur(deduction)}` : eur(0)}
          negative={deduction > 0}
          testId={`pb-platform-fee-${job.id}`}
        />
        <Row label="Your payout" value={eur(payout)} strong testId={`pb-payout-${job.id}`} />
        <Row label="Collect from customer" value={eur(payout)} strong testId={`pb-cash-to-collect-${job.id}`} />
      </div>

      <p className="text-xs mt-2.5 leading-snug text-amber-800" data-testid={`pb-note-${job.id}`}>
        Collect exactly <strong>{eur(payout)}</strong> from the customer — that is your full payout.
        The customer has already paid JustDeliver's online charges (€10 Booking Deposit + your {eur(deduction)} Driver
        Deduction), so nothing is owed to us afterwards. The €10 Booking Deposit is never taken from your payout.
      </p>
      <p className="text-[11px] mt-1.5 text-amber-900" data-testid={`pb-methods-${job.id}`}>
        You accept: <strong>{methodLabels}</strong>
      </p>
    </div>
  );
};

export default DriverPaymentBreakdown;
